import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { Drivers } from '@ionic/storage';
import { Storage } from '@ionic/storage-angular';
import { TLogApiRequest } from '@monorepo/types';

type TLogDatabase = {
  '[LOGS]': TLogApiRequest[];
  '[FINGERPRINT_KEYS]': Set<string>;
};

const DEFAUL_BATCH_SIZE = 600;
const LIMIT = 3000;
const THRESHOLDS_FOR_ALERT = 2999;

@Injectable({
  providedIn: 'root',
})
export class CustomLoggerDbService {
  #storage: Storage = new Storage({
    name: '__logDatabase',
    driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
  });

  #hasDatabaseCreated = false;

  #operationQueue: Promise<any> = Promise.resolve();

  constructor(private storage: Storage, private alertController: AlertController) { }

  async init() {
    this.#storage = await this.storage.create();
    // Initialize fingerprint set if not exists
    const fps = await this.#storage?.get('[FINGERPRINT_KEYS]');
    if (!fps) {
      await this.#storage?.set('[FINGERPRINT_KEYS]', new Set());
    }
    this.#hasDatabaseCreated = true;
  }

  async assertDBCreation() {
    if (this.#hasDatabaseCreated) return;
    await this.init();
  }

  #queueOperation<T>(operation: () => Promise<T>): Promise<T> {
    this.#operationQueue = this.#operationQueue.then(() => operation());
    return this.#operationQueue;
  }

  //single addition
  async addLog(log: TLogApiRequest) {
    this.#queueOperation(async () => this.#addLog('[LOGS]', log));
  }

  //bulk addition
  async addLogs(logs: TLogApiRequest[]) {
    this.#queueOperation(async () => this.#addLogs('[LOGS]', logs));
  }

  //bulk removal
  async extractLogsToBeFlushed(batchSize: number = DEFAUL_BATCH_SIZE) {
    return this.#queueOperation(async () => this.#getBatchToBeFlushed(batchSize));
  }

  async #getAllLogs(): Promise<TLogDatabase['[LOGS]']> {
    const logs = (await this.#storage?.get('[LOGS]')) || [];
    return logs;
  }

  async #addLogs<T extends keyof TLogDatabase>(key: T, logs: TLogApiRequest[]): Promise<void> {
    try {
      // Get current state
      const [fps, indexDBLogs] = await Promise.all([
        this.#getFingerprintSet(),
        this.#getAllLogs()
      ]);

      // Filter out existing logs using fingerprint set
      const newLogs = logs.filter(log => !fps.has(log.id));
      if (newLogs.length === 0) return;

      // Calculate total size and handle limit
      const totalSize = fps.size + newLogs.length;
      const logsToRemove = totalSize > LIMIT ? totalSize - LIMIT : 0;

      // Update fingerprint set
      if (logsToRemove > 0) {
        // Remove oldest fingerprints in bulk
        const oldLogs = indexDBLogs.slice(0, logsToRemove);
        oldLogs.forEach(log => fps.delete(log.id));
      }

      // Add new fingerprints in bulk using spread
      const newIds = newLogs.map(log => log.id);
      const updatedFps = new Set([...fps, ...newIds]);

      // Update logs array based on fingerprint set
      const finalLogs = [
        ...indexDBLogs.slice(logsToRemove),
        ...newLogs
      ];

      // Update storage in parallel
      const operations = [
        this.#storage?.set(key, finalLogs),
        this.#storage?.set('[FINGERPRINT_KEYS]', updatedFps)
      ];

      await Promise.all(operations);

      // Alert user if needed (non-blocking)
      if (updatedFps.size >= THRESHOLDS_FOR_ALERT && !navigator.onLine) {
        this.#alertUserForSyncLogging(updatedFps.size).catch(console.error);
      }
    } catch (error) {
      console.error('Error adding logs in bulk:', error);
    }
  }

  async #addLog<T extends keyof TLogDatabase>(key: T, log: TLogApiRequest): Promise<void> {
    try {
      // Get current state
      const [fps, indexDBLogs] = await Promise.all([
        this.#getFingerprintSet(),
        this.#getAllLogs()
      ]);

      // Early return if log already exists
      if (fps.has(log.id)) {
        return;
      }

      // Handle limit using fingerprint set size
      if (fps.size >= LIMIT) {
        const oldestLog = indexDBLogs.shift();
        if (oldestLog) {
          fps.delete(oldestLog.id);
        }
      }

      // Update fingerprint set and logs
      const updatedFps = new Set([...fps, log.id]);
      indexDBLogs.push(log);

      // Update storage in parallel
      const operations = [
        this.#storage?.set(key, indexDBLogs),
        this.#storage?.set('[FINGERPRINT_KEYS]', updatedFps)
      ];

      await Promise.all(operations);

      // Alert user if needed (non-blocking)
      if (updatedFps.size >= THRESHOLDS_FOR_ALERT && !navigator.onLine) {
        this.#alertUserForSyncLogging(updatedFps.size).catch(console.error);
      }
    } catch (error) {
      console.error('Error adding log:', error);
    }
  }

  async #getBatchToBeFlushed(batchSize: number) {
    const [fps, [topK, remainingLogs]] = await Promise.all([
      this.#getFingerprintSet(),
      this.#getTopKLogs(batchSize)
    ]);

    // Update fingerprint set by removing flushed logs
    const updatedFps = new Set(fps);
    topK.forEach(log => updatedFps.delete(log.id));

    // Update storage in parallel
    const operations = [
      this.#storage?.set('[LOGS]', remainingLogs),
      this.#storage?.set('[FINGERPRINT_KEYS]', updatedFps)
    ];

    await Promise.all(operations);
    return { topK, isLogsRemaining: !!remainingLogs.length };
  }

  //used in batch logging
  async #getTopKLogs(batchSize: number) {
    const logs = await this.#getAllLogs();
    const topk = logs.splice(0, Math.min(batchSize, logs.length));
    return [topk, logs];
  }

  async #alertUserForSyncLogging(logsSize: number = THRESHOLDS_FOR_ALERT) {
    const alert = await this.alertController.create({
      header: 'Log Syncing Alert',
      message: `Please go online to sync the logs and clear the logs. Current logsSize: ${logsSize}`,
      backdropDismiss: true,
      buttons: ['OK'],
    });

    await alert.present();
  }

  async #getFingerprintSet(): Promise<Set<string>> {
    const fps = await this.#storage?.get('[FINGERPRINT_KEYS]');
    return fps || new Set();
  }

}
